import { useState, type ComponentProps } from "react";
import { format, parseISO } from "date-fns";
import { CalendarDays } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { InlineDateField } from "@/components/waste/inline-date-field";
import { parseDay } from "@/lib/date-input";
import { cn } from "@/lib/utils";

/**
 * The date range both platform-admin screens read over (POS2-1138 decision 30).
 *
 * **Structure copied from `components/dashboard/period-control.tsx`, state deliberately not.**
 * That control is hard-wired to `useWasteFilters`, whose `DatePreset` union has no `yesterday` and
 * no `14d`, and whose `computeDayRange` resolves `7d` open-ended to the end of *today*. This screen
 * needs complete days only — every range ends yesterday — so a partial day at the end cannot make a
 * site look quieter than it is. Local `useState`, as `labelling.tsx` already does for its period.
 *
 * What *is* carried across, because each of those details is a bug somebody already found: the
 * segmented `aria-pressed` buttons, the last segment as a `PopoverTrigger`, seeding the pickers
 * from the applied range when it opens, blocking Apply on an inverted range with an inline reason
 * (POS2-1068), `w-80` on the popover because two date fields clip at `w-64`, and the `period-*`
 * testids.
 *
 * **English only** — the `/admin/*` carve-out from Phase 6.4.
 */

export type RangePreset = "yesterday" | "7d" | "14d" | "custom";

export type Range = { preset: RangePreset; from: string | null; to: string | null };

export const DEFAULT_RANGE: Range = { preset: "7d", from: null, to: null };

const SEGMENTS: { value: Exclude<RangePreset, "custom">; label: string }[] = [
  { value: "yesterday", label: "Yesterday" },
  { value: "7d", label: "Last 7 days" },
  { value: "14d", label: "Last 14 days" },
];

/** Yesterday, as a canonical `yyyy-MM-dd`. The latest day any range may reach. */
function yesterday(): string {
  const day = new Date();
  day.setDate(day.getDate() - 1);
  return format(day, "yyyy-MM-dd");
}

export function RangeControl({
  range,
  onChange,
  /** The window the API actually resolved, so the label names dates rather than restating a rule.
   *  Absent until the first response lands, which is when there is no window to name. */
  resolved,
}: {
  range: Range;
  onChange: (range: Range) => void;
  resolved?: { from: string; to: string };
}) {
  const [customOpen, setCustomOpen] = useState(false);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  function openCustom(open: boolean) {
    if (open) {
      // Seed the pickers with the applied range so reopening shows what is in force.
      setFrom(range.preset === "custom" ? (range.from ?? "") : "");
      setTo(range.preset === "custom" ? (range.to ?? "") : "");
    }
    setCustomOpen(open);
  }

  // Both sides are canonical "yyyy-MM-dd", so they compare lexicographically. An end before the
  // start matches nothing, which renders as "no records" — and on a churn screen that reads as
  // *this account is dead* rather than *your dates are backwards*. So it is a typo, not a query:
  // block Apply and say so (POS2-1068, and `use-explore-state.ts`'s guard from the other side).
  const invertedRange = from !== "" && to !== "" && to < from;
  const incomplete = from === "" || to === "";
  // Complete days only. The API refuses a range reaching today with a 400, and it is right to —
  // but a disabled button with a reason beside it is a better answer than a round trip that comes
  // back as an error toast. `InlineDateField` takes no `max`, and widening a component four other
  // pages use is not this ticket's work.
  const reachesToday = (from !== "" && from > yesterday()) || (to !== "" && to > yesterday());

  return (
    <div className="flex items-center gap-2">
      <div
        role="group"
        aria-label="Date range"
        className="flex items-center gap-0.5 rounded-md border border-gray-200 bg-white p-0.5"
      >
        {SEGMENTS.map((segment) => (
          <SegmentButton
            key={segment.value}
            label={segment.label}
            active={range.preset === segment.value}
            onClick={() => onChange({ preset: segment.value, from: null, to: null })}
          />
        ))}
        <Popover open={customOpen} onOpenChange={openCustom}>
          <PopoverTrigger asChild>
            <SegmentButton label="Custom" active={range.preset === "custom"} />
          </PopoverTrigger>
          {/* w-80, not w-64: two date fields side by side clip their last characters otherwise. */}
          <PopoverContent align="end" className="w-80 p-4">
            <p className="text-sm font-medium text-gray-900">Custom range</p>
            <p className="mt-1 text-xs text-gray-500">
              Complete days only, so a range ends yesterday at the latest.
            </p>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <InlineDateField
                label="Start"
                kind="date"
                placeholder="dd/mm/yyyy"
                value={from}
                onCommit={(raw) => setFrom(parseDay(raw) ?? "")}
                isValid={(raw) => parseDay(raw) != null}
                testId="period-custom-from"
              />
              <InlineDateField
                label="End"
                kind="date"
                placeholder="dd/mm/yyyy"
                value={to}
                onCommit={(raw) => setTo(parseDay(raw) ?? "")}
                isValid={(raw) => parseDay(raw) != null}
                testId="period-custom-to"
              />
            </div>
            {(invertedRange || reachesToday) && (
              <p className="mt-1.5 text-xs text-red-600" data-testid="period-custom-error">
                {invertedRange
                  ? "The start of the range is after its end."
                  : "A range covers complete days, so it ends yesterday at the latest."}
              </p>
            )}
            <Button
              size="sm"
              className="mt-3 w-full"
              disabled={invertedRange || incomplete || reachesToday}
              data-testid="period-custom-apply"
              onClick={() => {
                onChange({ preset: "custom", from, to });
                setCustomOpen(false);
              }}
            >
              Apply
            </Button>
          </PopoverContent>
        </Popover>
      </div>
      <span
        className="hidden items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-600 sm:inline-flex"
        data-testid="period-label"
      >
        <CalendarDays className="size-3.5 text-gray-400" aria-hidden />
        {resolved ? rangeLabel(resolved.from, resolved.to) : "Complete days only"}
      </span>
    </div>
  );
}

/**
 * The window in words.
 *
 * It names the **dates**, not just the preset, and that is load-bearing rather than decorative:
 * two sites in different timezones under one range label cover different instants, because each row
 * buckets these civil days in its own zone. Naming the days is what makes that legible instead of
 * mysterious.
 *
 * `parseISO` of a date-only string is local midnight — the calendar date itself, never shifted.
 */
export function rangeLabel(from: string, to: string): string {
  const day = (value: string, pattern: string) => format(parseISO(value), pattern);
  if (from === to) return day(from, "d MMM yyyy");
  const sameYear = from.slice(0, 4) === to.slice(0, 4);
  return `${day(from, sameYear ? "d MMM" : "d MMM yyyy")} to ${day(to, "d MMM yyyy")}`;
}

/** The query string these presets turn into. `custom` is the only branch that sends dates. */
export function rangeParams(range: Range): Record<string, string> {
  if (range.preset !== "custom") return { preset: range.preset };
  return { preset: "custom", from: range.from ?? "", to: range.to ?? "" };
}

function SegmentButton({
  label,
  active,
  ...props
}: { label: string; active: boolean } & ComponentProps<"button">) {
  return (
    <button
      type="button"
      aria-pressed={active}
      className={cn(
        "rounded px-2 py-1 text-xs font-medium transition-colors",
        active ? "bg-gray-100 text-gray-900" : "text-gray-500 hover:bg-gray-50 hover:text-gray-700",
      )}
      {...props}
    >
      {label}
    </button>
  );
}
